import { AxiosResponse } from "axios";
import { DealAction } from "store/actions/actions";
import {
  AltiusRes,
  Characteristic,
  CharacteristicData,
  CharacteristicsTableItem,
  CharacteristicValue,
  IAssetClass,
  ICharacteristicsManageData,
  ICharContact,
  ISubAssetClass,
  ISubCategory,
  PostDealCharacteristicsDataBody
} from "store/types";
import { toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import { toastify } from "utils/tyostify";

import { AsyncActions, AsyncActionsParams } from "../base";

export interface ICharacteristicActions {
  getDealCharacteristicsDataAction: (dealId: number) => Promise<{
    categories: string[];
    characteristics: Omit<Characteristic, "reduce_method" | "postfix" | "numerify" | "created_at" | "updated_at">[];
    characteristic_data: CharacteristicData[];
    contact: ICharContact[] | null;

    asset_class: IAssetClass;
    sub_asset_class: ISubAssetClass | null;
    subcategories: ISubCategory[];
    characteristic_subcategory_id: number | null;

    status: string;

    show_asset_class: boolean;
    show_sub_asset_class: boolean;

    type: string;
    has_sub_deals: boolean;
  }>;
  postDealCharacteristicsDataAction: (data: PostDealCharacteristicsDataBody) => Promise<string>;

  setCharacteristicsManageAction: (params: ICharacteristicsManageData | null) => void;

  getCharValuesAction: (search: string, charId: number) => Promise<string[]>;
  updateCharacteristicValueAction: (data: { value: string; id: number; deal_id: number }) => Promise<void>;
  updateCharacteristicDataAction: (data: {
    deal_id: number;
    characteristic_id: number;
    characteristic_data: {
      id: number | string;
      percentage: number;
      value: string;
      currency: null | string;
      is_default?: boolean;

      is_deleted: boolean;
    }[];
  }) => Promise<void>;

  getCharacteristicDefinitionsListAction: () => Promise<any>;
  uploadCharacteristicDefinitionsAction: (file: File) => Promise<void>;

  getCharacteristicsAction: (params?: { scope?: "deal" | "client" }) => Promise<Characteristic[]>;
  getCharacteristicsGeneralDataAction: () => Promise<{
    characteristic_list: Characteristic[];
    categories: string[];
  }>;
  createCharacteristicAction: (data: CharacteristicsTableItem) => Promise<Characteristic>;
  updateCharacteristicAction: (data: { id: number } & Partial<CharacteristicsTableItem>) => Promise<Characteristic>;
  deleteCharacteristicAction: (id: number) => Promise<number>;

  createSubCategoryAction: (name: string) => Promise<ISubCategory>;
  getSubCategoriesAction: () => Promise<ISubCategory[]>;
  downloadCharacteristicOptionsTemplateAction: () => Promise<void>;
  uploadCharacteristicOptionsAction: (data: { file: File }) => Promise<string[]>;
  validateStatusTransitionAction: (data: { deal_id: number; status_id: number }) => Promise<{
    valid: boolean;
    missing_fields: Array<{ id: number; name: string }>;
    target_status: string;
  }>;
  getDealsMissingCharacteristicAction: (data: {
    characteristicId: number;
  }) => Promise<{
    deals: Array<{ id: number; title: string; status_name: string }>;
  }>;
}

export class CharacteristicActions extends AsyncActions implements ICharacteristicActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getDealCharacteristicsDataAction: ICharacteristicActions["getDealCharacteristicsDataAction"] = async (
    dealId
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Awaited<ReturnType<ICharacteristicActions["getDealCharacteristicsDataAction"]>>>>({
      url: "/api/v0.0.3/characteristics/data",
      params: { deal_id: dealId }
    });

    return data;
  };

  public postDealCharacteristicsDataAction: ICharacteristicActions["postDealCharacteristicsDataAction"] = async (
    data
  ) => {
    const { data: res }: AxiosResponse<AltiusRes<{ message: string }>> = await this.api.post({
      url: "/api/v0.0.3/characteristics/data",
      data
    });
    return res.message;
  };

  public getCharValuesAction = async (search: string, charId: number): Promise<string[]> => {
    const {
      data: {
        data: { values }
      }
    }: AxiosResponse<AltiusRes<{ values: CharacteristicValue[] }>> = await this.api.get({
      url: `/api/v0.0.3/characteristics/${charId}/values`,
      params: { search }
    });

    return values.map((item) => (typeof item === "string" ? item : item.value));
  };

  public setCharacteristicsManageAction: ICharacteristicActions["setCharacteristicsManageAction"] = (payload) => {
    this.dispatch({
      type: DealAction.SetCharacteristicsManageData,
      payload
    });
  };

  public updateCharacteristicValueAction: ICharacteristicActions["updateCharacteristicValueAction"] = async (data) => {
    const { value, deal_id, id } = data;
    await this.api.patch<AltiusRes<{ value: string; characteristic_id: number }>>({
      url: `/api/v0.0.3/characteristics/${id}/data`,
      data: {
        value,
        deal_id
      }
    });
  };

  public updateCharacteristicDataAction: ICharacteristicActions["updateCharacteristicDataAction"] = async ({
    characteristic_id,
    ...body
  }) => {
    await this.api.patch<AltiusRes<{ value: string; characteristic_id: number }>>({
      url: `/api/v0.0.3/characteristics/${characteristic_id}/data`,
      data: body
    });
  };

  public getCharacteristicDefinitionsListAction = async (): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/investors/list/download",
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public uploadCharacteristicDefinitionsAction = async (file: File): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.3/investors/relations/upload",
      data: toFormData({ file })
    });
    toastify({ message: "Measure relations has been uploaded" });
  };

  public getCharacteristicsAction: ICharacteristicActions["getCharacteristicsAction"] = async (params?: { scope?: "deal" | "client" }) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Characteristic[]>>({
      url: "/api/v0.0.3/characteristics",
      params: params?.scope ? { scope: params.scope } : undefined
    });
    return data;
  };

  // ---------------------------------------------------------------------------
  // Client-characteristic CRUD endpoints (separate table from deal characteristics).
  // These hit the Laravel routes registered under /api/v0.0.3/client-characteristics.
  // ---------------------------------------------------------------------------
  public getClientCharacteristicsAction: ICharacteristicActions["getClientCharacteristicsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ClientCharacteristic[]>>({
      url: "/api/v0.0.3/client-characteristics"
    });
    return data;
  };

  public createClientCharacteristicAction: ICharacteristicActions["createClientCharacteristicAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ClientCharacteristic>>({
      url: "/api/v0.0.3/client-characteristics",
      data
    });
    return payload;
  };

  public updateClientCharacteristicAction: ICharacteristicActions["updateClientCharacteristicAction"] = async ({ id, ...body }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<ClientCharacteristic>>({
      url: `/api/v0.0.3/client-characteristics/${id}`,
      data: body
    });
    return payload;
  };

  public deleteClientCharacteristicAction: ICharacteristicActions["deleteClientCharacteristicAction"] = async (id) => {
    await this.api.delete({
      url: `/api/v0.0.3/client-characteristics/${id}`
    });
    return id;
  };

  public getCharacteristicsGeneralDataAction: ICharacteristicActions["getCharacteristicsGeneralDataAction"] =
    async () => {
      const {
        data: { data }
      } = await this.api.get<AltiusRes<{ characteristic_list: Characteristic[]; categories: string[] }>>({
        url: "/api/v0.0.3/deals/characteristics-general-data"
      });
      return data;
    };

  public createCharacteristicAction: ICharacteristicActions["createCharacteristicAction"] = async (data) => {
    const {
      status,
      data: { data: payload }
    } = await this.api.post<AltiusRes<Characteristic>>({
      url: "/api/v0.0.3/characteristics",
      data: data
    });

    if (status === 202) {
      toastify({
        message:
          "An characteristic with this name has been defined and archived, the system will reactive the characteristic."
      });
    }
    return payload;
  };

  public updateCharacteristicAction: ICharacteristicActions["updateCharacteristicAction"] = async ({ id, ...body }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<Characteristic>>({
      url: `/api/v0.0.3/characteristics/${id}`,
      data: body
    });
    return payload;
  };

  public deleteCharacteristicAction: ICharacteristicActions["deleteCharacteristicAction"] = async (id) => {
    await this.api.delete({
      url: `/api/v0.0.3/characteristics/${id}`
    });
    return id;
  };

  public getSubCategoriesAction: ICharacteristicActions["getSubCategoriesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ISubCategory[]>>({
      url: "/api/v0.0.3/characteristics/subcategories"
    });
    return data;
  };

  public createSubCategoryAction: ICharacteristicActions["createSubCategoryAction"] = async (name: string) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ISubCategory>>({
      url: "/api/v0.0.3/characteristics/subcategories",
      data: { name }
    });
    return payload;
  };

  public downloadCharacteristicOptionsTemplateAction: ICharacteristicActions["downloadCharacteristicOptionsTemplateAction"] =
    async () => {
      return this.api
        .get({
          url: "/api/v0.0.3/characteristics/options-template/download",
          responseType: "blob"
        })
        .then(downloadFileAxios);
    };

  public uploadCharacteristicOptionsAction: ICharacteristicActions["uploadCharacteristicOptionsAction"] = async (data: {
    file: File;
  }) => {
    const {
      data: { data: res }
    }: AxiosResponse<{ data: string[] }> = await this.api.post({
      url: "/api/v0.0.3/characteristics/options-template/import",
      data: toFormData(data),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return res;
  };

  public validateStatusTransitionAction: ICharacteristicActions["validateStatusTransitionAction"] = async (data) => {
    const {
      data: { data: res }
    } = await this.api.post<AltiusRes<{
      valid: boolean;
      missing_fields: Array<{ id: number; name: string }>;
      target_status: string;
    }>>({
      url: "/api/v0.0.3/characteristics/validate-status-transition",
      data
    });
    return res;
  };

  public getDealsMissingCharacteristicAction: ICharacteristicActions["getDealsMissingCharacteristicAction"] = async ({
    characteristicId
  }) => {
    const {
      data: { data: res }
    } = await this.api.get<AltiusRes<{
      deals: Array<{ id: number; title: string; status_name: string }>;
    }>>({
      url: `/api/v0.0.3/characteristics/${characteristicId}/deals-missing`
    });
    return res;
  };
}
